import { NetDebugChunkLabel } from '@/components/ap/devtools/NetDebugChunkLabel';
import { AppShell } from '@/components/ap/layout/AppShell';
import { BootLogoLoader } from '@/components/ap/logos/BootLogoLoader';
import {
  getManageTabFromPath,
  resolveExperienceShellSkeleton,
  TakeShellSkeleton,
} from '@/components/ap/skeletons';
import { Skeleton } from '@/components/ui/skeleton';
import CustomAuthenticated from '@/contexts/customAuthenticated';
import { useCurrentUser, useEnabledFeatures } from '@/hooks/dataHelpers/useCurrentUser';
import { resolveTakeSurface } from '@/pages/experiences/show/take/previewRouteGuard';
// APL-11850 (v3 parity): authProvider redirects to /error when the boot
// get_user call fails — i.e. exactly when the network is broken and a lazy
// chunk can't load. Static, straight from the file (NOT the @/pages/system
// barrel, which would statically absorb the other system pages).
import { ErrorPage as ErrorPageV4 } from '@/pages/system/ErrorPage';
import { selectEmbedded, useCommonStore } from '@/store/commonStore';
import { selectExperienceViewMode, useExperienceStore } from '@/store/experienceStore';
import { CanAccess } from '@refinedev/core';
import { CatchAllNavigate, NavigateToResource } from '@refinedev/react-router';
import { lazy, ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';

/**
 * APL-11850 (Slice 5) — the v4 route table (spec § Routing & navigation
 * registration). URL slugs are the historical v3 paths — deep links resolve
 * unchanged. R15b-b3: every route mounts a NATIVE v4 page; the `v1()` lazy
 * fallback machinery and the last v1 page components are gone (the live map
 * is `src/MIGRATION.md`).
 */

/**
 * Historical scope wrapper for v4 page routes — it carried the scoped-preflight
 * class until preflight went global (P2-48); now a boxless (`display: contents`)
 * pass-through kept to avoid churning every route element.
 */
const V4PageScope = ({ children }: { children: ReactNode }) => (
  <div className="contents">{children}</div>
);

/** Suspense fallback while a lazy page chunk downloads. */
const RouteLoading = () => (
  <div className="flex flex-col gap-4 p-6" data-testid="v4-route-loading">
    <Skeleton className="h-8 w-64" />
    <Skeleton className="h-4 w-96" />
    <Skeleton className="h-64 w-full" />
  </div>
);

/** Auth surfaces are centered-card pages — a page-shaped skeleton is the wrong
 *  shape for them, and it's what flashed on login/logout while the lazy auth
 *  chunk downloaded. They ride the same pulsating "A" as the boot auth gate
 *  (AuthPendingFallback) so boot → login and logout → login each read as one
 *  continuous loader. */
const isAuthSurfacePath = (pathname: string) =>
  pathname.startsWith('/auth/') || pathname.startsWith('/guest/join');

/** NA1-nav-loading — fallback for the OUTER Suspense boundary, which now only
 *  catches trees living OUTSIDE AppShell (take/manage/print/auth/embed; the
 *  in-shell boundary lives in AppShell around the Outlet). The experience
 *  deep routes get their shell-shaped skeleton; auth surfaces get the boot
 *  logo; everything else keeps the generic shape. */
const OutsideShellFallback = () => {
  const { pathname } = useLocation();
  if (isAuthSurfacePath(pathname)) return <BootLogoLoader />;
  const shellSkeleton = resolveExperienceShellSkeleton(pathname);
  // APL-11850: the take chunk downloads via native import() — invisible to
  // the net-debug fetch/XHR/axios patches — so the take-shell fallback
  // carries its own debug-gated label (renders null unless net debug is on).
  if (shellSkeleton && !getManageTabFromPath(pathname)) {
    return (
      <>
        {shellSkeleton}
        <NetDebugChunkLabel />
      </>
    );
  }
  return shellSkeleton ?? <RouteLoading />;
};

// R15b-b3: the `v1()` fallback machinery is GONE — no route mounts a v1 page
// component anymore (the last two, ExperienceShow + ExperienceLayout, retired
// with the atomic collapse).

// ── route page components ────────────────────────────────────────────────────

// Migrated (CT2-connectivity run, QUEUE 5.7b): v4 decomposed diagnostics page.
const ConnectivityTest = lazy(() =>
  import('@/pages/diagnosticTools').then((m) => ({ default: m.ConnectivityTestPage }))
);
// EM1-embedded (QUEUE 3.10e): the resource element is v4-native; EmbedLayout
// went native at R15b — owns the ?jwt= MB auth handoff (byte-untouched).
const EmbedResourcePage = lazy(() =>
  import('@/pages/embed').then((m) => ({ default: m.EmbedResourcePage }))
);
const EmbedLayout = lazy(() => import('@/pages/embed').then((m) => ({ default: m.EmbedLayout })));
// R15b-b3: the deep-route wrapper is NATIVE (full chrome re-home — audit §2).
const ExperienceLayout = lazy(() =>
  import('@/pages/experiences/ExperienceRouteLayout').then((m) => ({
    default: m.ExperienceRouteLayout,
  }))
);
// PR1-print (QUEUE 6.1): the print page is NATIVE v4 (self-scoped, outside
// AppShell). R15b-b3: new_export_pdf_layout GA — the v1 whole-page fallback
// is gone; every org renders the v4 layout.
const PrintAssessmentPageV4 = lazy(() => import('@/pages/print/PrintAssessmentPage'));
// Migrated (MU1-mobile-upload run, QUEUE 3.7d): native v4 offline-recovery
// route (thin shell — no take-tree hydration mounts).
const LocalSyncPageV4 = lazy(() =>
  import('@/pages/experiences/show/take/localSync/LocalSyncPage').then((m) => ({
    default: m.LocalSyncPage,
  }))
);
// Migrated (LM1-livemap run): native v4 superadmin live world map.
const LiveWorldMapPage = lazy(() =>
  import('@/pages/liveMap').then((m) => ({ default: m.LiveWorldMapPage }))
);
// Migrated (RM1-rooms run): v4 rooms CRUD + show world.
const RoomsPage = lazy(() => import('@/pages/rooms').then((m) => ({ default: m.RoomsPage })));
const RoomFormPage = lazy(() => import('@/pages/rooms').then((m) => ({ default: m.RoomFormPage })));
const RoomShowPage = lazy(() => import('@/pages/rooms').then((m) => ({ default: m.RoomShowPage })));
// Antd-free v3 utility page reused as-is (RS2-routes; reuse-not-fork) — the
// postMessage listener v2 embeds via iframe to log v3 out.
const CrossAppLogoutListener = lazy(() =>
  import('@/app/CrossAppLogoutListener').then((m) => ({
    default: m.CrossAppLogoutListener,
  }))
);
// Native v4 wildcard 404 (VG1-gaps) — replaced @refinedev/antd's
// ErrorComponent, the last unnamed antd mount in the v4 shell graph.
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);

// ── v4 pages (migrated — Slice 6 onward; scoreboard: src/MIGRATION.md) ───
// Lazy like the v1 fallbacks: each migrated page stays out of the App boot
// chunk. Every future migration copies this exact shape.

const DownloadAppsPage = lazy(() =>
  import('@/pages/downloadApps').then((m) => ({ default: m.DownloadAppsPage }))
);
const FeaturesPage = lazy(() =>
  import('@/pages/features').then((m) => ({ default: m.FeaturesPage }))
);
const AppReleasesPage = lazy(() =>
  import('@/pages/appReleases').then((m) => ({ default: m.AppReleasesPage }))
);
const AppReleaseFormPage = lazy(() =>
  import('@/pages/appReleases').then((m) => ({ default: m.AppReleaseFormPage }))
);
const FeatureFormPage = lazy(() =>
  import('@/pages/features').then((m) => ({ default: m.FeatureFormPage }))
);
const GroupsPage = lazy(() => import('@/pages/groups').then((m) => ({ default: m.GroupsPage })));
const UsersPage = lazy(() => import('@/pages/users').then((m) => ({ default: m.UsersPage })));
const UserFormPage = lazy(() => import('@/pages/users').then((m) => ({ default: m.UserFormPage })));
const GroupShowPage = lazy(() =>
  import('@/pages/groups').then((m) => ({ default: m.GroupShowPage }))
);
const GroupFormPage = lazy(() =>
  import('@/pages/groups').then((m) => ({ default: m.GroupFormPage }))
);
const OrgsPage = lazy(() => import('@/pages/orgs').then((m) => ({ default: m.OrgsPage })));
const OrgFormPage = lazy(() => import('@/pages/orgs').then((m) => ({ default: m.OrgFormPage })));
const SchoolsPage = lazy(() => import('@/pages/schools').then((m) => ({ default: m.SchoolsPage })));
// Routes sweep 1 (RS1-routes run).
const ProfilePage = lazy(() => import('@/pages/profile').then((m) => ({ default: m.ProfilePage })));
const StudentProfilePage = lazy(() =>
  import('@/pages/students').then((m) => ({ default: m.StudentProfilePage }))
);
const WhatsNewPage = lazy(() =>
  import('@/pages/whatsNew').then((m) => ({ default: m.WhatsNewPage }))
);
const AssessmentsCalendarPage = lazy(() =>
  import('@/pages/assessmentsCalendar').then((m) => ({ default: m.AssessmentsCalendarPage }))
);
const SubjectMergerPage = lazy(() =>
  import('@/pages/subjectMerger').then((m) => ({ default: m.SubjectMergerPage }))
);
const ResourceSheetPage = lazy(() =>
  import('@/pages/resourceSheet').then((m) => ({ default: m.ResourceSheetPage }))
);
// Routes sweep 2 (RS2-routes run) — auth + system cluster. All of these
// routes live OUTSIDE AppShell (NotFoundPage precedent). Login proper +
// register + OTP/QR joined native in the LG2-login run.
const ForgotPasswordPageV4 = lazy(() =>
  import('@/pages/auth').then((m) => ({ default: m.ForgotPasswordPage }))
);
const LoginPageV4 = lazy(() => import('@/pages/auth').then((m) => ({ default: m.LoginPage })));
const RegisterPageV4 = lazy(() =>
  import('@/pages/auth').then((m) => ({ default: m.RegisterPage }))
);
const EmailCodeLoginPageV4 = lazy(() =>
  import('@/pages/auth').then((m) => ({ default: m.EmailCodeLoginPage }))
);
const TextCodeLoginPageV4 = lazy(() =>
  import('@/pages/auth').then((m) => ({ default: m.TextCodeLoginPage }))
);
// R15b (batch-1 seam retirement): guest-join is now native — retired the v1
// AuthPage guest-login shell (the whole src/pages/login tree went unreachable).
const GuestJoinPageV4 = lazy(() =>
  import('@/pages/auth').then((m) => ({ default: m.GuestJoinPage }))
);
const UpdatePasswordPageV4 = lazy(() =>
  import('@/pages/auth').then((m) => ({ default: m.UpdatePasswordPage }))
);
const AuthCallbackPageV4 = lazy(() =>
  import('@/pages/auth').then((m) => ({ default: m.AuthCallbackPage }))
);
const DemoMaintenancePageV4 = lazy(() =>
  import('@/pages/system').then((m) => ({ default: m.DemoMaintenancePage }))
);
const UnsupportedVersionLayoutV4 = lazy(() =>
  import('@/pages/system').then((m) => ({ default: m.UnsupportedVersionLayout }))
);
const UnsupportedVersionPageV4 = lazy(() =>
  import('@/pages/system').then((m) => ({ default: m.UnsupportedVersionPage }))
);
// OrgSettingsPage is NOT wrapped in V4PageScope at its routes (documented in
// src/MIGRATION.md): the page owns its own `.force-light` scoping internally
// (historical — it hosted embedded v1 tab bodies).
const OrgSettingsPage = lazy(() =>
  import('@/pages/settings').then((m) => ({ default: m.OrgSettingsPage }))
);
// Programs tab family (AD-orgprogrammes run) — nested inside the settings
// shell's Outlet; native v4.
const OrgProgrammesTab = lazy(() =>
  import('@/pages/orgProgrammes').then((m) => ({ default: m.OrgProgrammesTab }))
);
const OrgProgrammeGradesPanel = lazy(() =>
  import('@/pages/orgProgrammes').then((m) => ({ default: m.GradesPanel }))
);
const OrgProgrammeSubjectsPanel = lazy(() =>
  import('@/pages/orgProgrammes').then((m) => ({ default: m.SubjectsPanel }))
);
const OrgProgrammeTermsPanel = lazy(() =>
  import('@/pages/orgProgrammes').then((m) => ({ default: m.TermsListPanel }))
);
const OrgProgrammeTermsFormPage = lazy(() =>
  import('@/pages/orgProgrammes').then((m) => ({ default: m.TermsFormPage }))
);
// Batch A (clean-CRUD superadmin modules).
const PlansPage = lazy(() => import('@/pages/plans').then((m) => ({ default: m.PlansPage })));
const PlanFormPage = lazy(() => import('@/pages/plans').then((m) => ({ default: m.PlanFormPage })));
const BoardsPage = lazy(() => import('@/pages/boards').then((m) => ({ default: m.BoardsPage })));
const BoardFormPage = lazy(() =>
  import('@/pages/boards').then((m) => ({ default: m.BoardFormPage }))
);
const TagsPage = lazy(() => import('@/pages/tags').then((m) => ({ default: m.TagsPage })));
const TagFormPage = lazy(() => import('@/pages/tags').then((m) => ({ default: m.TagFormPage })));
const ProgrammesPage = lazy(() =>
  import('@/pages/programmes').then((m) => ({ default: m.ProgrammesPage }))
);
const ProgrammeFormPage = lazy(() =>
  import('@/pages/programmes').then((m) => ({ default: m.ProgrammeFormPage }))
);
const AnnouncementsPage = lazy(() =>
  import('@/pages/announcements').then((m) => ({ default: m.AnnouncementsPage }))
);
const AnnouncementFormPage = lazy(() =>
  import('@/pages/announcements').then((m) => ({ default: m.AnnouncementFormPage }))
);
const QuestionBanksPage = lazy(() =>
  import('@/pages/questionBanks').then((m) => ({ default: m.QuestionBanksPage }))
);
const QuestionBankFormPage = lazy(() =>
  import('@/pages/questionBanks').then((m) => ({ default: m.QuestionBankFormPage }))
);
// Batch B (custom-form superadmin modules).
const FeedbacksPage = lazy(() =>
  import('@/pages/feedbacks').then((m) => ({ default: m.FeedbacksPage }))
);
const IntegrationsPage = lazy(() =>
  import('@/pages/integrations').then((m) => ({ default: m.IntegrationsPage }))
);
const IntegrationFormPage = lazy(() =>
  import('@/pages/integrations').then((m) => ({ default: m.IntegrationFormPage }))
);
const RubricsPage = lazy(() => import('@/pages/rubrics').then((m) => ({ default: m.RubricsPage })));
const RubricFormPage = lazy(() =>
  import('@/pages/rubrics').then((m) => ({ default: m.RubricFormPage }))
);
// Student growth (GW1-growth run, APL-11550): district-QB-author module,
// dark-gated `student_growth` (resourcePermissions) — v4-only, no v1 route.
const StudentGrowthList = lazy(() =>
  import('@/pages/studentGrowth').then((m) => ({ default: m.StudentGrowthList }))
);
const StudentGrowthWizard = lazy(() =>
  import('@/pages/studentGrowth').then((m) => ({ default: m.StudentGrowthWizard }))
);
const StudentGrowthEvaluate = lazy(() =>
  import('@/pages/studentGrowth').then((m) => ({ default: m.StudentGrowthEvaluate }))
);
const PromptsPage = lazy(() => import('@/pages/prompts').then((m) => ({ default: m.PromptsPage })));
const PromptFormPage = lazy(() =>
  import('@/pages/prompts').then((m) => ({ default: m.PromptFormPage }))
);
// Standard sets (AV-standard-sets run): drill-down family — list, create
// form, collection show (content areas), content-area show (topics tree on
// APTree flatBlob/blob).
const StandardSetsPage = lazy(() =>
  import('@/pages/standardSets').then((m) => ({ default: m.StandardSetsPage }))
);
const StandardSetFormPage = lazy(() =>
  import('@/pages/standardSets').then((m) => ({ default: m.StandardSetFormPage }))
);
const StandardSetShowPage = lazy(() =>
  import('@/pages/standardSets').then((m) => ({ default: m.StandardSetShowPage }))
);
const ContentAreaShowPage = lazy(() =>
  import('@/pages/standardSets').then((m) => ({ default: m.ContentAreaShowPage }))
);
// Curriculum (S3a run). CurriculumShowPage is NOT wrapped in V4PageScope at
// its route (same documented deviation as OrgSettingsPage): the show page
// owns its own `.force-light` scoping internally (historical — it hosted
// embedded v1 tab bodies) — see src/MIGRATION.md.
const CurriculumPage = lazy(() =>
  import('@/pages/curriculum').then((m) => ({ default: m.CurriculumPage }))
);
const CurriculumShowPage = lazy(() =>
  import('@/pages/curriculum').then((m) => ({ default: m.CurriculumShowPage }))
);
// Help page (grading-scales+audit-logs+help run): idiomatic v4 recompose —
// static Zendesk link list, no data fetching, so a full page rebuild rather
// than a v1 fallback.
const HelpPage = lazy(() => import('@/pages/help').then((m) => ({ default: m.HelpPage })));
// Global AI usage (ai-usage tab run): native v4 recompose shared with the
// org-settings AI-USAGE tab (src/pages/aiUsage/AIUsage.tsx) — the org tab
// and this standalone superadmin page render the same component tree.
const GlobalAIUsagePage = lazy(() =>
  import('@/pages/aiUsage').then((m) => ({ default: m.GlobalAIUsagePage }))
);
// My Assessments (AO-my-assessments run): role/mode-split routes — see the
// route wrapper components below the fallback machinery.
const MyAssessmentsPage = lazy(() =>
  import('@/pages/myAssessments').then((m) => ({ default: m.MyAssessmentsPage }))
);
const MyAssessmentFormPage = lazy(() =>
  import('@/pages/myAssessments').then((m) => ({ default: m.MyAssessmentFormPage }))
);
// Student/parent list variant (BC-student-assessments run).
const StudentAssessmentsPage = lazy(() =>
  import('@/pages/studentAssessments/StudentAssessmentsPage').then((m) => ({
    default: m.StudentAssessmentsPage,
  }))
);
// Library (AP-library run): explore + search + filters + district form.
// QB1-bank-items (APL-11553): bank-first IA for non-district qb-authors —
// /library/resources redirects to the cards entry; resources/:qbId is the
// native bank ITEMS tab (the v1 LibraryResourcesList island mount retired).
const LibraryPageV4 = lazy(() =>
  import('@/pages/library').then((m) => ({ default: m.LibraryPage }))
);
const LibraryAssessmentFormPage = lazy(() =>
  import('@/pages/library').then((m) => ({ default: m.LibraryAssessmentFormPage }))
);
// Overview (AQ-overview run): today's org-wide assessments, monitor-in-new-tab
// rows. Role-split like /assessments — student/parent get the native student
// recompose (VG1-gaps; StudentAssessmentRow, Join islands intact).
const OverviewPageV4 = lazy(() =>
  import('@/pages/districtOverview').then((m) => ({ default: m.OverviewPage }))
);
const OverviewStudentPageV4 = lazy(() =>
  import('@/pages/districtOverview').then((m) => ({ default: m.OverviewStudentPage }))
);
// Experience manage shell (AS-experience-manage run): deliver/monitor/analytics
// tabs of /assessments/:id/* — role/mode/tab-split via ExperienceShowRoute
// below. Self-scoped page (route lives OUTSIDE AppShell).
const ExperienceManagePageV4 = lazy(() =>
  import('@/pages/experiences').then((m) => ({ default: m.ExperienceManagePage }))
);
// Teacher preview (CG-take-preview run): the v4 take-shape surface, mounted
// when the store view mode flips to apPreviewTest (slice-7 preview-first
// gate). Direct module import — keeps the preview chunk out of the manage one.
const ExperienceTakePreviewPageV4 = lazy(() =>
  import('@/pages/experiences/show/take/ExperienceTakePreviewPage').then((m) => ({
    default: m.ExperienceTakePreviewPage,
  }))
);
// STUDENT REVIEWER seat (PV2-reviewer-seat): the native scoped grade world for
// a peer-review student reviewer at the v3 URL (`/grade?reviewer=true`).
const ExperienceReviewerPageV4 = lazy(() =>
  import('@/pages/experiences/show/reviewer/ExperienceReviewerPage').then((m) => ({
    default: m.ExperienceReviewerPage,
  }))
);
// STUDENT take (FL1-flip, QUEUE 3.7): the v4 exam screen for real students.
// Own chunk — students never pay for the manage/preview chunks and vice versa.
const ExperienceTakePageV4 = lazy(() =>
  import('@/pages/experiences/show/take/ExperienceTakePage').then((m) => ({
    default: m.ExperienceTakePage,
  }))
);
// Gradebook (AT-gradebook run): the gradebook (v3 module `gradebook-v3`)
// recomposed native v4. Legacy `/gradebook` deleted at LD1 (gradebook_v3 GA);
// the old URL redirects here.
const GradebookPageV4 = lazy(() =>
  import('@/pages/gradebook').then((m) => ({ default: m.GradebookPage }))
);

// ── Shell-level redirect helpers (duplicated from AppV1.tsx — tiny, no antd,
// and importing them from the v1 shell would drag AppV1 into the v4 graph) ──

const V2UrlsRedirect = () => {
  const location = useLocation();
  const currentUrl = location.pathname + location.search;

  let redirectUrl = '';

  if (currentUrl.includes('/tests/')) {
    // Extract just the UUID from the URL
    const uuidMatch = currentUrl.match(/\/tests\/([^/]+)/);
    const uuid = uuidMatch ? uuidMatch[1] : '';
    const urlAfterUuid = currentUrl.split(`/tests/${uuid}`)[1]?.split('?')[0] || '';

    // Build redirect URL
    redirectUrl = `/assessments/${uuid}`;

    const validRedirectTabs = ['deliver', 'monitor', 'grade', 'analytics'];
    const matchingTab = validRedirectTabs.find((tab) => urlAfterUuid.includes(tab));

    if (matchingTab) {
      redirectUrl = redirectUrl.concat(urlAfterUuid);
    }

    // Preserve query parameters if any
    const queryString = location.search;
    if (queryString) {
      redirectUrl = redirectUrl + queryString;
    }
  } else {
    redirectUrl = '/';
  }

  return <Navigate to={redirectUrl} replace />;
};

// ── My Assessments route wrappers (AO-my-assessments run) ──────────────────
// Several form surfaces are deliberately NOT migrated — those branches mount
// the v1 pages inside V1IslandScope; everything else gets the v4 pages.
// Fail-closed: any branch not explicitly migrated keeps its v3 behavior.

/** /assessments — v4 list for every role (student/parent variant since the
 *  BC-student-assessments run). R15 executed the recorded dev KILL
 *  (2026-07-08(h)): the MB-iframe EMBEDDED student/parent v1 list is gone —
 *  embedded rides the native page; the take flow past the list is untouched. */
const MyAssessmentsListRoute = () => {
  const currentUser = useCurrentUser();
  const isStudentOrParent = currentUser?.role === 'student' || currentUser?.role === 'parent';
  if (isStudentOrParent) {
    return (
      <V4PageScope>
        <StudentAssessmentsPage />
      </V4PageScope>
    );
  }
  return (
    <V4PageScope>
      <MyAssessmentsPage />
    </V4PageScope>
  );
};

/** DISTRICT qb-authors get the native district form (SB1-stragglers-b —
 *  retired the last v1 ExperienceForm route mounts; they never reach
 *  /assessments in practice, CanAccess redirects them to the library).
 *  ?mode=import is NATIVE since IM1-import, ?mode=ai since
 *  AA1-ai-assessments (both in MyAssessmentFormPage). SCHOOL qb-authors
 *  render the native v4 form since BD-feedback5; advanced-tag flag orgs
 *  natively since AZ-feedback2. */
const useDistrictAssessmentForm = () => {
  const currentUser = useCurrentUser();
  return currentUser?.role === 'qb_author' && currentUser?.org?.org_type === 'district';
};

const MyAssessmentFormRoute = () => {
  const districtForm = useDistrictAssessmentForm();
  return (
    <V4PageScope>
      {districtForm ? <LibraryAssessmentFormPage /> : <MyAssessmentFormPage />}
    </V4PageScope>
  );
};

/** /overview — role split, both branches native (VG1-gaps retired the
 *  student/parent V1IslandScope): teacher-shaped rows for staff, the
 *  StudentAssessmentRow recompose for student/parent (an exotic `overviews`
 *  grant meets the same student shape v3's ExperienceListItem rendered). */
const OverviewRoute = () => {
  const currentUser = useCurrentUser();
  const isStudentOrParent = currentUser?.role === 'student' || currentUser?.role === 'parent';
  if (isStudentOrParent) {
    return (
      <V4PageScope>
        <OverviewStudentPageV4 />
      </V4PageScope>
    );
  }
  return (
    <V4PageScope>
      <OverviewPageV4 />
    </V4PageScope>
  );
};

// ── Experience show route wrapper ───────────────────────────────────────────
// /assessments/:id/* — the deep show route (OUTSIDE AppShell, full-screen
// under the NATIVE ExperienceRouteLayout since R15b-b3). No v1 branch remains:
//   - student/parent → v4 take page; student reviewer context → v4 reviewer.
//   - teacher preview (incl. embedded PC1 flip, paper iframe, one-by-one) →
//     v4 preview page.
//   - create/deliver/monitor/grade/analytics tab → v4 ExperienceManagePage.
//   - bare path → redirect to the create tab (v1 default-tab law).
//   - unresolved role → skeleton; embedded teacher-manage/reviewer → notice.

// v3 ExperienceManage.getActiveKeyFromPath now lives in
// @/components/ap/skeletons (getManageTabFromPath) — shared with the
// route-shaped loading fallbacks (R2-G62); still never imported from
// @/pages/experiences (would pull the lazy manage chunk into the main bundle).

/** R15b-b3 — embedded teacher-manage / reviewer contexts (dev KILL
 *  2026-07-08(h)): graceful notice instead of the retired v1 tree. */
const EmbedUnsupportedNotice = () => {
  const { t } = useTranslation();
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-2 bg-ap-canvas p-8 text-center text-foreground"
      data-testid="embed-unsupported-notice"
    >
      <p className="text-lg font-medium">
        {t('ExperienceShow.you_are_not_authorized_to_access_this_assessment')}
      </p>
    </div>
  );
};

const ExperienceShowRoute = () => {
  const currentUser = useCurrentUser();
  const embedded = useCommonStore(selectEmbedded);
  const experienceViewMode = useExperienceStore(selectExperienceViewMode);
  const location = useLocation();

  // Take-family surfaces (R15b-b3 matrix — previewRouteGuard.ts): teacher
  // PREVIEW (incl. paper iframe + one-by-one pager, post-fetch branches),
  // students/parents (take host), student reviewer seat. No v1 fallback
  // remains: unresolved role waits on a skeleton, embedded teacher-manage/
  // reviewer contexts render the dev-kill notice.
  const takeSurface = resolveTakeSurface({
    role: currentUser?.role,
    embedded,
    experienceViewMode,
    mobileUploadSession: location.search.includes('mobileUploadMode=true'),
    // PV2-reviewer-seat: the v3 ReviewButton URL (`/grade?reviewer=true`).
    reviewerContext:
      location.pathname.includes('/grade') || new URLSearchParams(location.search).has('reviewer'),
  });
  if (takeSurface === 'pending-role') {
    return <TakeShellSkeleton testId="experience-route-pending-role" />;
  }
  // 'embed-view' serves the read-only presentation ONLY on the /embed/:uuid
  // pane (J22-B02); embedded teacher-manage deep links here keep the notice.
  if (takeSurface === 'embed-unsupported' || takeSurface === 'embed-view') {
    return <EmbedUnsupportedNotice />;
  }
  if (takeSurface === 'v4-preview') {
    return <ExperienceTakePreviewPageV4 />;
  }
  if (takeSurface === 'v4-reviewer') {
    // PV2-reviewer-seat: post-fetch gate inside the page (v3
    // student_reviewer_mode law) — non-reviewer /grade hits render the native
    // take/results surface, drafts the native unauthorized screen.
    return <ExperienceReviewerPageV4 />;
  }
  if (takeSurface === 'v4-take') {
    // FL1-flip: experience-dependent fail-closes (draft) live INSIDE the
    // page, post-fetch.
    return <ExperienceTakePageV4 />;
  }

  const tab = getManageTabFromPath(location.pathname);
  if (tab === 'grade') {
    // `/grade/:euId` deep links open the v4 GRADING MODAL since the
    // BY-grade-modal run; teacher-side peer-review grade native since PV1.
    return <ExperienceManagePageV4 />;
  }
  if (tab === 'deliver' || tab === 'monitor' || tab === 'analytics' || tab === 'create') {
    return <ExperienceManagePageV4 />;
  }

  // Bare path /assessments/:id — v1 ExperienceManage defaulted the active tab
  // to 'create' (getActiveKeyFromPath fallback); native = redirect to it.
  // J25-M02: forward navigation state (backTo referrer) through the redirect —
  // <Navigate> without `state` drops it (history.state.usr goes null).
  return <Navigate to="create" replace state={location.state} />;
};

// ── Library / qb-scoped form route wrappers ─────────────────────────────────
// /library/tests/:qbId/create|edit is a DISTRICT-qb-author surface in v3 (the
// create button is district-gated); /questionbanks/show/:libraryId/experience/*
// are legacy deep-link-only mounts. Both fully native since SB1-stragglers-b:
// district qb-authors get the district form, everyone else the school/teacher
// form (the route bank param pre-fills the bank on create).

const LibraryAssessmentFormRoute = () => {
  const districtForm = useDistrictAssessmentForm();
  return (
    <V4PageScope>
      {districtForm ? <LibraryAssessmentFormPage /> : <MyAssessmentFormPage />}
    </V4PageScope>
  );
};

export const V4Routes = () => {
  const currentUser = useCurrentUser();
  const enabledFeatures = useEnabledFeatures();
  const notDistrictQbAuthor =
    currentUser?.org?.org_type !== 'district' && currentUser?.role === 'qb_author';

  const NavigateToDefaultPage = () => {
    let defaultPage = '/assessments';
    if (currentUser?.role === 'superadmin') {
      defaultPage = '/orgs';
    }
    if (currentUser?.role === 'support') {
      defaultPage = '/orgs';
    }
    if (currentUser?.org?.org_type === 'district') {
      defaultPage = '/library/explore';
    }
    if (notDistrictQbAuthor) {
      defaultPage = '/library/tests';
    }
    return <Navigate to={defaultPage} replace />;
  };

  return (
    <Suspense fallback={<OutsideShellFallback />}>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<NavigateToDefaultPage />} />
          {/* Migrated (AO-my-assessments run): v4 list + teacher/admin +
              school-qb-author form (BD-feedback5); ai / import modes and
              district qb-authors stay v1 via the role/mode-split wrappers. */}
          <Route path="/assessments">
            <Route
              index
              element={
                <CanAccess
                  resource="experiences"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <MyAssessmentsListRoute />
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess
                  resource="experiences"
                  action="create"
                  fallback={<NavigateToDefaultPage />}
                >
                  <MyAssessmentFormRoute />
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess
                  resource="experiences"
                  action="edit"
                  fallback={<NavigateToDefaultPage />}
                >
                  <MyAssessmentFormRoute />
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (CT2-connectivity): no CanAccess gate — v1 parity (any logged-in role). */}
          <Route path="/diagnostic-tools">
            <Route
              index
              element={
                <V4PageScope>
                  <ConnectivityTest />
                </V4PageScope>
              }
            />
          </Route>

          <Route path="/overview">
            <Route
              index
              element={
                <CanAccess resource="overviews" action="list" fallback={<NavigateToDefaultPage />}>
                  <OverviewRoute />
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (batch A): v4 CRUD — APCollection list, APFormShell forms. */}
          <Route path="/questionbanks">
            <Route
              index
              element={
                <CanAccess
                  resource="questionbanks"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <QuestionBanksPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess
                  resource="questionbanks"
                  action="create"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <QuestionBankFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess
                  resource="questionbanks"
                  action="edit"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <QuestionBankFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="show/:libraryId/experience/create"
              element={
                <CanAccess
                  resource="questionbank_experiences"
                  action="create"
                  fallback={<NavigateToDefaultPage />}
                >
                  <LibraryAssessmentFormRoute />
                </CanAccess>
              }
            />
            <Route
              path="show/:libraryId/experience/edit/:id"
              element={
                <CanAccess
                  resource="questionbank_experiences"
                  action="edit"
                  fallback={<NavigateToDefaultPage />}
                >
                  <LibraryAssessmentFormRoute />
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (batch B): v4 CRUD — APCollection list, APFormShell with
              the recomposed criteria-builder grid. */}
          <Route path="/rubrics">
            <Route
              index
              element={
                <CanAccess resource="rubrics" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <RubricsPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess resource="rubrics" action="create" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <RubricFormPage mode="create" />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess resource="rubrics" action="edit" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <RubricFormPage mode="edit" />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Student growth (GW1-growth run) — net-new v4 module (APL-11550;
              no v1 counterpart route). All routes gate on the single BE
              `list` grant (spec parity). */}
          <Route path="/student-growth">
            <Route
              index
              element={
                <CanAccess
                  resource="student_growths"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <StudentGrowthList />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="new"
              element={
                <CanAccess
                  resource="student_growths"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <StudentGrowthWizard />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path=":id/edit"
              element={
                <CanAccess
                  resource="student_growths"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <StudentGrowthWizard />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path=":id/evaluate"
              element={
                <CanAccess
                  resource="student_growths"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <StudentGrowthEvaluate />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (AP-library run + QB1-bank-items): v4 explore/search/
              filters + district form + native bank home (cards → Assessments /
              Items). The create/edit form routes are role-split (district
              qb-author → v4 form; everyone else fail-closes onto the v1
              form). */}
          <Route path="/library">
            <Route
              path="tests"
              element={
                <CanAccess resource="librarys" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <LibraryPageV4 />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="explore"
              element={
                <CanAccess resource="librarys" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <LibraryPageV4 />
                  </V4PageScope>
                </CanAccess>
              }
            />
            {/* Bare resources has no bank context — bank-scoping is the point,
                so redirect to the bank-cards entry (APL-11553). Deep links to
                resources/:qbId still work (below). */}
            <Route path="resources" element={<Navigate to="/library/tests" replace />} />
            {/* Bank ITEMS tab, scoped to the route-fixed bank (native
                BankItemsPage inside the LibraryPage shell). */}
            <Route
              path="resources/:qbId"
              element={
                <CanAccess resource="librarys" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <LibraryPageV4 />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="tests/:qbId"
              element={
                <CanAccess resource="librarys" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <LibraryPageV4 />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="tests/:qbId/create"
              element={
                <CanAccess resource="librarys" action="create" fallback={<NavigateToDefaultPage />}>
                  <LibraryAssessmentFormRoute />
                </CanAccess>
              }
            />
            <Route
              path="tests/:qbId/edit/:id"
              element={
                <CanAccess resource="librarys" action="edit" fallback={<NavigateToDefaultPage />}>
                  <LibraryAssessmentFormRoute />
                </CanAccess>
              }
            />
          </Route>
          <Route path="/common_assessments">
            <Route
              path="tests"
              element={
                <CanAccess
                  resource="common_assessments"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <LibraryPageV4 isCommonAssessmentBank={true} />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="explore"
              element={
                <CanAccess
                  resource="common_assessments"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <LibraryPageV4 isCommonAssessmentBank={true} />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* LD1: legacy /gradebook deleted (gradebook_v3 GA) — old links land on v4. */}
          <Route path="/gradebook" element={<Navigate to="/gradebook-v3" replace />} />
          <Route path="/gradebook-v3">
            <Route
              index
              element={
                <CanAccess
                  resource="gradebook-v3"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <GradebookPageV4 />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Student Profile Analytics — mounted unconditionally so deep links
              resolve; the page guards on the flag, BE Pundit is authoritative.
              Migrated (RS1-routes run). */}
          <Route
            path="/students/:uuid"
            element={
              <CanAccess resource="students" action="show" fallback={<NavigateToDefaultPage />}>
                <V4PageScope>
                  <StudentProfilePage />
                </V4PageScope>
              </CanAccess>
            }
          />
          {/* Migrated (BA-feedback3): district admin's child-orgs list, v4
              native (nested rows + Edlink sync). */}
          <Route path="/schools">
            <Route
              index
              element={
                <CanAccess resource="schools" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <SchoolsPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (users run): v4 CRUD — APCollection list with role
              quickFilters + selection/bulk archive, APFormShell create/edit. */}
          <Route path="/users">
            <Route
              index
              element={
                <CanAccess resource="users" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <UsersPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess resource="users" action="edit" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <UserFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess resource="users" action="create" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <UserFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (groups run): v4 CRUD — APCollection list, APShowPage
              with embedded students collection, APFormShell create/edit. */}
          <Route path="/groups">
            <Route
              index
              element={
                <CanAccess resource="groups" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <GroupsPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess resource="groups" action="create" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <GroupFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess resource="groups" action="edit" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <GroupFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="show/:id"
              element={
                <CanAccess resource="groups" action="show" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <GroupShowPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (RM1-rooms run): v4 rooms — APCollection list,
              APFormPage create/edit, APShowPage show + membership dialogs. */}
          <Route path="/rooms">
            <Route
              index
              element={
                <CanAccess resource="rooms" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <RoomsPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess resource="rooms" action="create" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <RoomFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess resource="rooms" action="edit" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <RoomFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="show/:id"
              element={
                <CanAccess resource="rooms" action="show" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <RoomShowPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (orgs+settings run): v4 tab shell serving the admin
              entry; tab bodies are split native-v4 / v1-embed — see
              src/MIGRATION.md. No V4PageScope: the page scopes itself. */}
          <Route
            path="/settings"
            element={
              <CanAccess resource="org_settings" action="list" fallback={<NavigateToDefaultPage />}>
                <OrgSettingsPage />
              </CanAccess>
            }
          >
            <Route index element={null} />
            <Route path="general" element={null} />
            {/* Former v1 embeds are gone. Bulk import is native (APL-9671);
                integrations remains a coming-soon settings panel. */}
            <Route path="bulk-import" element={null} />
            <Route path="integrations" element={null} />
            <Route path="enable-py" element={null} />
            <Route path="permissions" element={null} />
            <Route path="audit-logs" element={null} />
            <Route path="standard-sets" element={null} />
            <Route path="grading-scales" element={null} />
            <Route path="oneroster-import" element={null} />
            <Route path="ai-usage" element={null} />
            <Route path="usage" element={null} />
            <Route path="features" element={null} />
            <Route path="app-releases" element={null} />
            <Route
              path="programs"
              element={
                <CanAccess
                  resource="org_programmes"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <OrgProgrammesTab />
                </CanAccess>
              }
            />
            <Route path="programs/:programId" element={<OrgProgrammesTab />}>
              <Route index element={<Navigate to="subjects" replace />} />
              <Route path="grades" element={<OrgProgrammeGradesPanel />} />
              <Route path="subjects" element={<OrgProgrammeSubjectsPanel />} />
              <Route path="terms">
                <Route
                  index
                  element={
                    <CanAccess resource="terms" action="list" fallback={<NavigateToDefaultPage />}>
                      <OrgProgrammeTermsPanel />
                    </CanAccess>
                  }
                />
                <Route
                  path="create"
                  element={
                    <CanAccess
                      resource="terms"
                      action="create"
                      fallback={<NavigateToDefaultPage />}
                    >
                      <OrgProgrammeTermsFormPage />
                    </CanAccess>
                  }
                />
                {/* URL shape unchanged from v1 (`edit/123`); the param is
                    `:id` so APFormPage's route-driven mode/id inference works. */}
                <Route
                  path="edit/:id"
                  element={
                    <CanAccess resource="terms" action="edit" fallback={<NavigateToDefaultPage />}>
                      <OrgProgrammeTermsFormPage />
                    </CanAccess>
                  }
                />
              </Route>
            </Route>
          </Route>
          <Route path="/help">
            <Route
              index
              element={
                <CanAccess resource="helps" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <HelpPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (RS1-routes run). */}
          <Route path="/resource-sheets">
            <Route
              index
              element={
                <CanAccess
                  resource="resource_sheets"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <ResourceSheetPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          <Route path="/ai-usage">
            <Route
              index
              element={
                <CanAccess
                  resource="global_ai_usage"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <GlobalAIUsagePage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (LM1-livemap run). */}
          <Route path="/live-map">
            <Route
              index
              element={
                <CanAccess
                  resource="live_world_map"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <LiveWorldMapPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (RS1-routes run). */}
          <Route path="/assessments-calendar">
            <Route
              index
              element={
                <CanAccess
                  resource="assessments_calendar"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <AssessmentsCalendarPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          <Route path="/admin/subject-merger">
            <Route
              index
              element={
                <CanAccess
                  resource="subject_merger"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <SubjectMergerPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (features run): v4 CRUD; create/edit are full pages
              (APFormShell — the v4 default), route-driven so the v3 deep links
              keep resolving. */}
          <Route path="/features">
            <Route
              index
              element={
                <CanAccess resource="features" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <FeaturesPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess resource="features" action="create" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <FeatureFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess resource="features" action="edit" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <FeatureFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* APL-10453 — centralized app upload + version management
              (superadmin). Replaces the retired /manual-uploads surface. */}
          <Route path="/app-releases">
            <Route
              index
              element={
                <CanAccess
                  resource="app_releases"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <AppReleasesPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess
                  resource="app_releases"
                  action="create"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <AppReleaseFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess
                  resource="app_releases"
                  action="edit"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <AppReleaseFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (Y-announcements-form run): v4 list + create/edit form —
              closes the Batch A partial now the tiptap toolbar-v4 entry and
              datetime composite field clear the prior gates. Video upload is
              deliberately NOT ported (see src/MIGRATION.md § Batch A). */}
          <Route path="/announcements">
            <Route
              index
              element={
                <CanAccess
                  resource="announcements"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <AnnouncementsPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess
                  resource="announcements"
                  action="create"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <AnnouncementFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess
                  resource="announcements"
                  action="edit"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <AnnouncementFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (RS1-routes run). */}
          <Route path="/whats-new">
            <Route
              index
              element={
                <CanAccess resource="whats_new" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <WhatsNewPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          <Route path="/download-apps">
            <Route
              index
              element={
                <CanAccess
                  resource="download_apps"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  {/* Slice 6 — first migrated page: v4 rebuild, not the v1
                      fallback (spec § Surface — Phase-1 pilot page). */}
                  <V4PageScope>
                    <DownloadAppsPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (RS1-routes run). No CanAccess gate — v1 parity: the
              route is reachable by every authenticated role. */}
          <Route path="/profile">
            <Route
              index
              element={
                <V4PageScope>
                  <ProfilePage />
                </V4PageScope>
              }
            />
          </Route>
          <Route path="/orgs">
            {/* Migrated (orgs+settings run): v4 list on APCollection.
                Create/edit migrated in the U-orgs-form run (OrgFormPage). */}
            <Route
              index
              element={
                <CanAccess resource="orgs" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <OrgsPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess resource="orgs" action="create" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <OrgFormPage mode="create" />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess resource="orgs" action="edit" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <OrgFormPage mode="edit" />
                  </V4PageScope>
                </CanAccess>
              }
            />
            {/* Migrated (orgs+settings run): superadmin entry to the same v4
                tab shell. No V4PageScope — the page scopes itself. */}
            <Route
              path=":orgId/settings"
              element={
                <CanAccess
                  resource="orgs"
                  action="list" // depends on list permission for superadmin/support
                  fallback={<NavigateToDefaultPage />}
                >
                  <OrgSettingsPage />
                </CanAccess>
              }
            >
              <Route index element={null} />
              <Route path="general" element={null} />
              {/* Former v1 embeds are gone. Bulk import is native (APL-9671);
                  integrations remains a coming-soon settings panel. */}
              <Route path="bulk-import" element={null} />
              <Route path="integrations" element={null} />
              <Route path="enable-py" element={null} />
              <Route path="permissions" element={null} />
              <Route path="audit-logs" element={null} />
              <Route path="standard-sets" element={null} />
              <Route path="grading-scales" element={null} />
              <Route path="oneroster-import" element={null} />
              <Route path="ai-usage" element={null} />
              <Route path="usage" element={null} />
              <Route path="features" element={null} />
              <Route path="app-releases" element={null} />
              <Route
                path="programs"
                element={
                  <CanAccess
                    resource="org_programmes"
                    action="list"
                    fallback={<NavigateToDefaultPage />}
                  >
                    <OrgProgrammesTab />
                  </CanAccess>
                }
              />
              <Route path="programs/:programId" element={<OrgProgrammesTab />}>
                <Route index element={<Navigate to="subjects" replace />} />
                <Route path="grades" element={<OrgProgrammeGradesPanel />} />
                <Route path="subjects" element={<OrgProgrammeSubjectsPanel />} />
                <Route path="terms">
                  <Route
                    index
                    element={
                      <CanAccess
                        resource="terms"
                        action="list"
                        fallback={<NavigateToDefaultPage />}
                      >
                        <OrgProgrammeTermsPanel />
                      </CanAccess>
                    }
                  />
                  <Route
                    path="create"
                    element={
                      <CanAccess
                        resource="terms"
                        action="create"
                        fallback={<NavigateToDefaultPage />}
                      >
                        <OrgProgrammeTermsFormPage />
                      </CanAccess>
                    }
                  />
                  {/* URL shape unchanged from v1 (`edit/123`); the param is
                      `:id` so APFormPage's route-driven mode/id inference works. */}
                  <Route
                    path="edit/:id"
                    element={
                      <CanAccess
                        resource="terms"
                        action="edit"
                        fallback={<NavigateToDefaultPage />}
                      >
                        <OrgProgrammeTermsFormPage />
                      </CanAccess>
                    }
                  />
                </Route>
              </Route>
            </Route>
          </Route>
          {/* Migrated (batch A): v4 CRUD — APCollection list, APFormShell forms. */}
          <Route path="/boards">
            <Route
              index
              element={
                <CanAccess resource="boards" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <BoardsPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess resource="boards" action="create" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <BoardFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess resource="boards" action="edit" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <BoardFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (batch A): v4 CRUD — APCollection list, APFormShell forms. */}
          <Route path="/programmes">
            <Route
              index
              element={
                <CanAccess resource="programmes" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <ProgrammesPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess
                  resource="programmes"
                  action="create"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <ProgrammeFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess resource="programmes" action="edit" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <ProgrammeFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (batch A): v4 CRUD — APCollection list, APFormShell forms. */}
          <Route path="/plans">
            <Route
              index
              element={
                <CanAccess resource="plans" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <PlansPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess resource="plans" action="create" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <PlanFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess resource="plans" action="edit" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <PlanFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (batch B): v4 CRUD — APCollection view="table" with
              confirm-first publish switch, APFormShell form. */}
          <Route path="/prompts">
            <Route
              index
              element={
                <CanAccess resource="prompts" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <PromptsPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess resource="prompts" action="create" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <PromptFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess resource="prompts" action="edit" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <PromptFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (batch B): v4 read-only table — APCollection
              view="table" + APFilterPanel + row-detail Dialog. */}
          <Route path="/feedbacks">
            <Route
              index
              element={
                <CanAccess resource="feedbacks" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <FeedbacksPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (batch A): v4 CRUD — APCollection list, APFormShell forms. */}
          <Route path="/tags">
            <Route
              index
              element={
                <CanAccess resource="tags" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <TagsPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess resource="tags" action="create" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <TagFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess resource="tags" action="edit" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <TagFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (AV-standard-sets run): v4 drill-down — APCollection
              list, APFormPage create, show pages, APTree topics
              (flatBlob + blob persistence). */}
          <Route path="/standard-sets">
            <Route
              index
              element={
                <CanAccess
                  resource="standard_sets"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <StandardSetsPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess
                  resource="standard_sets"
                  action="create"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <StandardSetFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path=":id"
              element={
                <CanAccess
                  resource="standard_sets"
                  action="show"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <StandardSetShowPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path=":standardCollectionId/content-areas/:id"
              element={
                <CanAccess
                  resource="standard_sets"
                  action="show"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <ContentAreaShowPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (S3a run): v4 list on APCollection; the show page is
              the v4 tab shell with native details/subjects/grades tabs +
              v1-embedded standard-sets/tags/resource-sheets tabs. No
              V4PageScope on the show route — the page scopes itself. */}
          <Route path="/curriculum">
            <Route
              index
              element={
                <CanAccess resource="curriculum" action="list" fallback={<NavigateToDefaultPage />}>
                  <V4PageScope>
                    <CurriculumPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path=":id"
              element={
                <CanAccess resource="curriculum" action="show" fallback={<NavigateToDefaultPage />}>
                  <CurriculumShowPage />
                </CanAccess>
              }
            />
          </Route>
          {/* Migrated (batch B): v4 CRUD — APCollection view="list", APFormShell
              with the dynamic integration-fields array + entity-config JSON
              editor (edit mode). */}
          <Route path="/integrations">
            <Route
              index
              element={
                <CanAccess
                  resource="integrations"
                  action="list"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <IntegrationsPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="create"
              element={
                <CanAccess
                  resource="integrations"
                  action="create"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <IntegrationFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
            <Route
              path="edit/:id"
              element={
                <CanAccess
                  resource="integrations"
                  action="edit"
                  fallback={<NavigateToDefaultPage />}
                >
                  <V4PageScope>
                    <IntegrationFormPage />
                  </V4PageScope>
                </CanAccess>
              }
            />
          </Route>
          {/* Fully themed since IK1/7.3 (the migratedPaths force-light
              deviation self-healed). */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="/embed" element={<EmbedLayout />}>
          <Route path=":uuid/*" element={<EmbedResourcePage />} />
        </Route>

        <Route
          path="/assessments"
          element={
            <CustomAuthenticated
              key="authenticated-experience-show"
              fallback={<CatchAllNavigate to="/auth/login" />}
            >
              <ExperienceLayout />
            </CustomAuthenticated>
          }
        >
          <Route path="/assessments">
            {/* Standalone sync page — must come BEFORE the catch-all
                ":id/*" route, otherwise the catch-all swallows it
                and ExperienceShow mounts instead. NATIVE since MU1 (3.7d). */}
            <Route path=":id/local-sync" element={<LocalSyncPageV4 />} />
            <Route path=":id/*" element={<ExperienceShowRoute />} />
          </Route>
        </Route>

        <Route
          path="/e/tests/:id/*"
          element={
            <CustomAuthenticated
              key="authenticated-v2-urls-redirect-e"
              fallback={<CatchAllNavigate to="/auth/login" />}
            >
              <V2UrlsRedirect />
            </CustomAuthenticated>
          }
        />
        <Route
          path="/u/tests/:id/*"
          element={
            <CustomAuthenticated
              key="authenticated-v2-urls-redirect-u"
              fallback={<CatchAllNavigate to="/auth/login" />}
            >
              <V2UrlsRedirect />
            </CustomAuthenticated>
          }
        />

        <Route
          path="/print"
          element={
            <CustomAuthenticated
              key="authenticated-experience-print"
              fallback={<CatchAllNavigate to="/auth/login" />}
            >
              <Outlet />
            </CustomAuthenticated>
          }
        >
          <Route path="assessments/:uuid" element={<PrintAssessmentPageV4 />} />
        </Route>

        {/* Migrated (RS2-routes run) — self-scoped, see the lazy-import block. */}
        <Route path="/maintenance/resetting-demo" element={<DemoMaintenancePageV4 />} />

        {/* PKCE callback — must be unauthenticated; macOS AASA intercepts before this loads.
            Migrated (RS2-routes run). */}
        <Route path="/auth/callback" element={<AuthCallbackPageV4 />} />

        {/* Migrated (RS2-routes run): native notice; QuitBtn/UpdateAppButton
            stay v1 force-light islands inside the page. */}
        <Route element={<UnsupportedVersionLayoutV4 />}>
          <Route path="/unsupported" element={<UnsupportedVersionPageV4 />} />
        </Route>

        <Route
          element={
            <CustomAuthenticated key="authenticated-outer" fallback={<Outlet />}>
              <NavigateToResource />
            </CustomAuthenticated>
          }
        >
          {/* Migrated (RS2-routes + LG2-login + R15b runs): native pages on the
              SAME refine auth hooks (authProvider/authStore wires untouched).
              /guest/join is native since R15b (guest session creation wire =
              experiences_get_with_join_code → handleAuthSuccess, byte-parity). */}
          <Route path="/auth/login" element={<LoginPageV4 />} />
          <Route path="/auth/register" element={<RegisterPageV4 />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPageV4 />} />
          <Route path="/auth/set-password" element={<UpdatePasswordPageV4 />} />
          <Route path="/auth/email-code-login" element={<EmailCodeLoginPageV4 />} />
          <Route path="/auth/text-code-login" element={<TextCodeLoginPageV4 />} />
          <Route path="/guest/join" element={<GuestJoinPageV4 />} />
        </Route>

        {/* Migrated (RS2-routes run). */}
        <Route path="/error" element={<ErrorPageV4 />} />

        <Route path="/seb_quit.html" element={<>SEB Quit</>} />

        <Route path="/cross-app-logout" element={<CrossAppLogoutListener />} />
      </Routes>
    </Suspense>
  );
};
